/**
 * API-клиент метрик сотрудников colver (`/workspaces/{ws}/user-metrics` и др.).
 * Паттерн идентичен `shared/api/queue-resolutions`: axios + `X-Tests-User-Sid` в dev.
 */
import axios from 'axios';
import {readStoredSid} from '@api/auth';
import type {
  UserActivityDaysRequest,
  UserActivityDaysResponse,
  ActivityEventsInput,
  HeartbeatInput,
  UserMetricsRequest,
  UserMetricsResponse,
  UserSessionsRequest,
  UserSessionsResponse,
} from './types';

const http = axios.create({
  baseURL: import.meta.env.VITE_MEEREC_BASE ?? '/api/user/meerec/v1',
});

const fallbackDevSid = import.meta.env.VITE_DEV_USER_SID;
http.interceptors.request.use((config) => {
  const sid = readStoredSid() ?? fallbackDevSid;
  if (sid) config.headers.set('X-Tests-User-Sid', sid);
  return config;
});

export const userMetricsClient = {
  /** Метрики видимых сотрудников за период (глубина — по доступу). */
  async metrics(
    workspaceSid: string,
    body: UserMetricsRequest,
    signal?: AbortSignal,
  ): Promise<UserMetricsResponse> {
    const {data} = await http.post<UserMetricsResponse>(
      `/workspaces/${workspaceSid}/user-metrics`,
      body,
      {signal},
    );
    return data;
  },

  /** Сессии одного сотрудника (только админ/оператор). */
  async sessions(
    workspaceSid: string,
    body: UserSessionsRequest,
    signal?: AbortSignal,
  ): Promise<UserSessionsResponse> {
    const {data} = await http.post<UserSessionsResponse>(
      `/workspaces/${workspaceSid}/user-sessions`,
      body,
      {signal},
    );
    return data;
  },

  async activityDays(
    workspaceSid: string,
    body: UserActivityDaysRequest,
    signal?: AbortSignal,
  ): Promise<UserActivityDaysResponse> {
    const {data} = await http.post<UserActivityDaysResponse>(
      `/workspaces/${workspaceSid}/user-activity-days`,
      body,
      {signal},
    );
    return data;
  },

  /** Heartbeat видимой активной вкладки (время в сервисе). */
  async heartbeat(input: HeartbeatInput): Promise<void> {
    await http.post('/users/self/heartbeat', input);
  },

  /**
   * Продуктовые события пачкой. `keepalive` — при уходе со вкладки/страницы:
   * запрос доживает до конца, даже если страница уже закрывается.
   */
  async activityEvents(
    input: ActivityEventsInput,
    {keepalive = false}: {keepalive?: boolean} = {},
  ): Promise<void> {
    await http.post(
      '/users/self/activity-events',
      input,
      keepalive
        ? {adapter: 'fetch', fetchOptions: {keepalive: true}}
        : undefined,
    );
  },

  /**
   * Выход для метрик: вызывать ДО logout шлюза (после него JWT уже нет). Logout
   * Gatekeeper'а до бэка не доходит. Короткий таймаут — выход не ждёт метрики.
   */
  async endSession(): Promise<void> {
    await http.post('/users/self/session-end', undefined, {timeout: 2000});
  },
};
